<template>
  <component
    :is="href ? 'a' : to ? nuxtLink : 'button'"
    v-bind="{'href': href, 'disabled': disabled ? disabled : null, 'aria-label': ariaLabel}"
    :target="external ? '_blank' : ''"
    :rel="external ? 'noreferrer' : ''"
    :to="to"
    :no-prefetch="true"
    :type="!to ? type : null"
    :direction="direction"
    :class="[
      $style['button'],
      { [$style['button--loading']]: loading },
      { [$style['button--disabled']]: disabled },
      { [$style['button--compact']]: compact },
      { [$style['button--visually-disabled']]: visuallyDisabled },
      { [$style['button--bold']]: bold },
      { [$style[`button--no-padding--${noPadding}`]]: noPadding },
      { [$style[`button--${shape}`]]: shape },
      { [$style[`button--${color}`]]: color },
      { [$style[`button--${size}`]]: size },
      { [$style[`button--font-size-${fontSize}`]]: fontSize },
      { [$style['button--with-icon']]: iconLeft || iconRight || iconOnly},
      { [$style['button--with-icon--left']]: iconLeft },
      { [$style['button--with-icon--right']]: iconRight },
      { [$style['button--with-postfix']]: postfix },
      { [$style['button--with-border']]: border },
      { [$style[`button--before-input`]]: beforeInput },
      { [$style[`button--after-input`]]: afterInput },
      { [$style[`button--full-width-${fullWidth}`]]: fullWidth }
    ]">
    <SvgIcon
      v-if="iconLeft"
      :class="[$style['button__icon'], $style['button__icon-left']]"
      :size="size"
      :name="iconLeft" />
    <span
      v-if="!iconOnly"
      :class="$style['button__content']">
      <slot />
    </span>
    <SvgIcon
      v-else
      :class="[$style['button__icon'], $style['button__icon-only']]"
      :size="size"
      :name="iconOnly" />
    <SvgIcon
      v-if="iconRight"
      :class="[$style['button__icon'], $style['button__icon-right']]"
      :size="size"
      :name="iconRight" />
    <span
      v-if="postfix"
      :class="$style['button__postfix']">{{ postfix }}</span>
  </component>
</template>
<script setup>
const nuxtLink = resolveComponent('nuxt-link')

defineProps({
  to: {
    type: [String, Object, Boolean],
    default: null
  },
  href: {
    type: [String, Object, Boolean],
    default: undefined
  },
  ariaLabel: {
    type: String,
    default: undefined
  },
  loading: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  external: {
    type: Boolean,
    default: false
  },
  visuallyDisabled: {
    // Looks disabled, but still emits click events
    type: Boolean,
    default: false
  },
  bold: {
    type: Boolean,
    default: false
  },
  compact: {
    type: Boolean,
    default: false
  },
  noPadding: {
    type: [Boolean, String],
    default: null,
    validator (value) {
      return [null, 'all', 'x', 'y'].includes(value)
    }
  },
  type: {
    type: String,
    default: null
  },
  iconOnly: {
    type: [Boolean, String],
    default: null
  },
  iconLeft: {
    type: String,
    default: null
  },
  iconRight: {
    type: String,
    default: null
  },
  fullWidth: {
    type: String,
    default: null,
    validator (value) {
      return [null, 'all', 'tablet', 'mobile'].includes(value)
    }
  },
  postfix: {
    type: [String, Number],
    default: null
  },
  border: {
    type: Boolean,
    default: false
  },
  shape: {
    type: String,
    default: null,
    validator (value) {
      return [null, '', 'default', 'pill', 'round'].includes(value)
    }
  },
  size: {
    type: String,
    default: null,
    validator (value) {
      return ['', 'small', 'large'].includes(value)
    }
  },
  fontSize: {
    type: String,
    default: null,
    validator (value) {
      return ['', 'small'].includes(value)
    }
  },
  color: {
    type: String,
    default: null,
    validator (value) {
      return [
        '',
        'blue',
        'cta',
        'ghost',
        'white',
        'error',
        'link',
        'link-active',
        'gold',
        'unavailable',
        'visually-disabled'
      ].includes(value)
    }
  },
  // Use this to place a button directly before an input
  beforeInput: {
    type: Boolean,
    default: false
  },
  // Use this to place a button directly after an input
  afterInput: {
    type: Boolean,
    default: false
  },
  // Used by the ionic router. Sets the route animation
  direction: {
    type: String,
    default: 'forward'
  }
})
</script>
<style lang="scss" module>
.button {
  appearance: none;
  align-items: center;
  justify-content: center;
  background-color: $grey-lighter;
  border: 0;
  border-radius: $border-radius;
  box-shadow: none;
  color: $black;
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  height: $spacing * 6;
  padding: 0 $spacing * 3;
  position: relative;
  line-height: $line-height;
  text-align: center;
  font-family: $font-base;
  font-size: $font-size;
  font-weight: $font-weight-normal;
  min-width: $spacing * 5;
  user-select: none;
  text-decoration: none;
  will-change: background, opacity, color, transform;
  transition: all $transition-time $transition-easing;

  &:focus-visible {
    box-shadow: $focus-visible-small;
  }

  @media (hover: hover) {
    &:hover {
      background-color: $grey-light;
      color: $black;
    }
  }

  &:active:not([disabled], .button--unavailable) {
    // background-color: $grey-lightest;
    transform: scale(.95);
  }

  &__content {
    display: inline-flex;
    justify-content: center;
    white-space: nowrap;
    width: 100%;
  }

  &--loading {
    &,
    &:hover {
      opacity: 0.8;
      pointer-events: none;
      position: relative;
      color: $grey-light;
      overflow: hidden;
    }

    &::after {
      animation: loading 1s ease;
      animation-delay: 250ms; // PRevents animation on fast actions
      animation-iteration-count: infinite;
      background: rgba($grey, 0.2);
      content: '';
      display: block;
      height: $spacing * 0.5;
      position: absolute;
      top: 0;
      left: 0;
      right: 100%;
      width: auto;
    }
  }

  &--visually-disabled,
  &[disabled],
  &--disabled {
    pointer-events: none;
    opacity: 0.25;
  }

  &--visually-disabled {
    pointer-events: all;
  }

  // Color variants
  &--cta {
    background-color: $black;
    color: $white;

    @media (hover: hover) {
      &:hover {
        background-color: $grey-darker;
        color: $white;
      }
    }

    &:focus {
      background-color: $grey-darker;
      color: $white;
    }

    &:active {
      background-color: $grey-dark;
    }

    &.button--loading {
      color: $grey-dark;

      &::after {
        background: $grey-dark;
      }
    }
  }

  &--gold,
  &--gold:hover,
  &--gold:focus,
  &--gold:active {
    border-color: $gold;
    background-color: $gold;
    color: rgba($white, 0.8);

    i {
      animation: pop 1s ease;
      will-change: transform;
    }
  }

  &--ghost {
    background-color: rgba($white, 0.5);
    backdrop-filter: blur(3px);

    // border: 1px solid rgba($grey, 0.5);
    color: $black;
    fill: $black;

    &:active,
    &:hover {
      background-color: rgba($grey, 0.2);
    }
  }

  &--white {
    background-color: $white;
    box-shadow: none;
    color: $black;

    &:active,
    &:hover {
      background-color: $grey-lightest;
    }
  }

  &--blue {
    // background-color: transparent;
    color: $denim-dark;
  }

  &--link-active,
  &--link {
    background-color: transparent;
    border-color: transparent;
    box-shadow: none;
    color: $grey-dark;
    text-decoration: none;

    &.pull-left {
      margin-left: $spacing * -1.5;
    }

    &:active,
    &:focus,
    &:hover {
      background-color: transparent;
      color: $black;
    }
  }

  &--link-active {
    color: $black;
    text-decoration: underline;
  }

  &--with-border {
    border: 1px solid $grey-light;
  }

  // Stated
  &--bold {
    font-family: $font-heading;
    font-weight: 700;
    font-size: $font-size-l;
    line-height: $font-size;
    text-transform: uppercase;
  }

  &--compact {
    padding-left: $spacing * 1.5;
    padding-right: $spacing * 1.5;

    @media (min-width: #{$breakpoint-mobile}) {
      padding-left: $spacing * 2;
      padding-right: $spacing * 2;
    }
  }

  &--unavailable {
    color: $grey-dark;
    cursor: default;

    position: relative;

    &::after {
      content: '';
      display: block;
      position: absolute;
      left: $spacing * 0.5;
      right: $spacing * 0.5;
      top: 50%;
      width: auto;
      height: 1px;
      border-top: 1px solid $grey;
      transform: rotate(-20deg);
    }
  }

  &--error {
    border: $red;
    color: $red;

    &:focus,
    &:hover {
      background-color: rgba($red, 0.05);
      color: $red;
    }

    &:active {
      background-color: rgba($red, 0.1);
    }
  }

  // Shapes variants
  &--pill {
    border-radius: 99999px;
  }

  &--round {
    border-radius: 100%;
    padding: 0;
    width: $spacing * 6;
  }

  &--square {
    aspect-ratio: 1/1;
    width: $spacing * 6;
    padding: 0;
    height: auto;
  }

  // Size variants
  &--small {
    height: $spacing * 4;
    font-size: $font-size-s;
    line-height: $font-size-s;

    &:focus-visible {
      box-shadow: $focus-visible-small;
    }
  }

  &--large {
    height: $spacing * 7;
    padding: 0 $spacing * 4;
    font-size: $font-size-m;
    line-height: $font-size-m;

    @media(max-width: $breakpoint-tiny) {
      padding: 0 $spacing * 2;
      }
  }

  // Font size variant
  &--font-size-small {
    font-size: $font-size-s;
  }

  // With icon
  &--with-icon {
    .button__icon-left,
    .button__icon-right {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
    }

    &--left {
      padding-left: $spacing * 6;

      .button__icon-left {
        left: $spacing * 2;
      }
    }

    &--right {
      padding-right: $spacing * 6;

      .button__icon-right {
        right: $spacing * 2;
      }
    }
  }

  // Postfix
  &--with-postfix {
    display: flex;
    width: 100%;
    justify-content: space-between;

    .button__content {
      text-align: center;
      justify-content: center;
      width: calc(80% - $spacing * 2);
      padding: $spacing;
    }

    .button__postfix {
      align-items: center;
      justify-content: center;
      font-family: $font-base;
      font-size: $font-size-s;
      letter-spacing: 0;
      font-weight: 400;
      background-color: rgba($white, 0.1);
      color: $grey;
      display: inline-flex;
      position: absolute;
      right: 0;
      top: 0;
      bottom: 0;
      padding: $spacing;
      min-width: calc(20% + $spacing * 2);
    }
  }

  &--full-width-all {
    display: flex;
    width: 100%;

    &.button--with-postfix {
      .button__content {
        width: 100%;
        padding-right: $spacing * 10;
      }

      .button__postfix {
        min-width: $spacing * 10;
        padding: 0 $spacing * 2;
      }
    }
  }

  &--full-width-mobile {
    @media (max-width: #{$breakpoint-mobile}) {
      display: flex;
      margin-bottom: $spacing;
      width: 100%;
    }
  }

  &--full-width-tablet {
    @media (max-width: #{$breakpoint-tablet}) {
      display: flex;
      margin-bottom: $spacing;
      width: 100%;
    }
  }

  // Padding variants
  &--no-padding {
    &--all {
      padding: 0;
    }

    &--x {
      padding-left: 0;
      padding-right: 0;
    }

    &--y {
      padding-top: 0;
      padding-bottom: 0;
    }
  }

  // Combination go here
  /* stylelint-disable no-duplicate-selectors */
  &--round {
    &.button--small {
      width: $spacing * 4;
      height: $spacing * 4;
      min-width: $spacing * 4;
    }

    &.button--large {
      padding: 0;
      height: $spacing * 7;
      width: $spacing * 7;
    }

    &.button--loading {
      &::after {
        height: 100%;
      }
    }
  }

  &--pill {
    .button__icon-left {
      left: $spacing * 2;
    }

    .button__icon-right {
      right: $spacing * 2;
    }
  }

  &--bold {
    &.button--large {
      font-size: $font-size-l;

      @media(max-width: $breakpoint-tiny) {
        font-size: $font-size-m;
      }
    }
  }

  &--large {
    .button__icon-left {
      left: $spacing * 2;
    }

    .button__icon-right {
      right: $spacing * 2;
    }
  }

  /* stylelint-disable no-descending-specificity */
  &--small {
    &.button--with-icon--left {
      padding-left: $spacing * 3.5;
      padding-right: $spacing * 2;
    }

    &.button--with-icon--right {
      padding-right: $spacing * 3.5;
      padding-left: $spacing * 2;
    }

    .button__icon-left {
      left: $spacing;
    }

    .button__icon-right {
      right: $spacing;
    }

    .button__icon {
        width: $spacing * 2.5;
        height: $spacing * 2.5
    }
  }

  &--cta {
    .button__postfix {
      color: $white;
    }

    &.button--with-border {
      border-color: $black;
    }
  }

  &--before-input,
  &--after-input {
    padding: $spacing;
  }

  &--before-input {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  &--after-input {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
}

@keyframes loading {
  0% {
    left: 0;
    right: 100%;
  }

  49% {
    right: 0;
  }

  50% {
    left: 0;
    right: 0;
  }

  100% {
    left: 100%;
    right: 0;
  }
}</style>
